/**
 * Result Popup Manager
 *
 * This module manages the final result popup that displays the processed image
 * and lets users select a size before adding to cart.
 */

class ResultPopupManager {
  constructor() {
    // State variables
    this.resultPopupShown = false;
    this.resultImageUrl = null;
    this.selectedSize = "S"; // Default selected size

    this.selectedSizewatermark = false;

    // Size aspect ratios
    this.sizeAspectRatios = {
      S: 20 / 30, // 20x30"
      M: 30 / 40, // 30x40"
      L: 50 / 70, // 50x70"
    };

    // Initialize variant IDs
    this.initializeVariantIds();

    // Detect template type - pixar or face-swap
    this.templateType = this.detectTemplateType();
    console.log(`Detected template type: ${this.templateType}`);

    // Make this instance globally available
    window.resultPopupManager = this;

    console.log("Result Popup Manager initialized");
  }

  /**
   * Initialize variant IDs based on template type and try to get from product data
   */
  initializeVariantIds() {
    // Check multiple conditions for pet template
    const isPetTemplate = window.isPetTemplate || 
                         window.template === "product.pet" ||
                         window.template === "product.pixar-gemini" ||
                         window.template === "product.superhero-gemini" ||
                         location.href.includes("pet-pixar-portrait") ||
                         location.href.includes("/products/pet") ||
                         location.href.includes("/products/pixar-gemini") ||
                         location.href.includes("/products/superhero-gemini") ||
                         document.querySelector('[data-template="product.pet"]') ||
                         document.querySelector('[data-template="product.pixar-gemini"]') ||
                         document.querySelector('[data-template="product.superhero-gemini"]');
    
    // Try to get variant IDs from product meta or form data
    const productVariants = this.getProductVariantsFromPage();
    
    if (productVariants && Object.keys(productVariants).length > 0) {
      console.log("Using variant IDs from page data:", productVariants);
      this.variantIds = productVariants;
    } else if (isPetTemplate) {
      console.log("Pet template detected - using hardcoded pet variant IDs");
      // Hardcoded variant IDs for pet products as fallback
      this.variantIds = {
        S: "54386981110108", // Pet product - Size S (20x30 cm)
        M: "54386981142876", // Pet product - Size M (30x40 cm)  
        L: "54386981175644", // Pet product - Size L (50x70 cm)
      };
    } else {
      console.log("Standard template detected - using hardcoded standard variant IDs");
      // Hardcoded variant IDs for standard products as fallback
      this.variantIds = {
        S: "54224438722908", // Standard product - Size S
        M: "54215893451100", // Standard product - Size M
        L: "54215893483868", // Standard product - Size L
      };
    }
    
    console.log("Final variant IDs:", this.variantIds);
  }

  /**
   * Try to extract variant IDs from the page's product data
   */
  getProductVariantsFromPage() {
    const variants = {};
    
    // Method 1: Check for product JSON in script tags
    try {
      const productScript = document.querySelector('script[type="application/json"][data-product-json]');
      if (productScript) {
        const productData = JSON.parse(productScript.textContent);
        if (productData.variants) {
          productData.variants.forEach(variant => {
            // Map variant options to our size keys
            const sizeOption = variant.options?.find(opt => 
              opt === "20x30 cm" || opt === "30x40 cm" || opt === "50x70 cm" ||
              opt === "S" || opt === "M" || opt === "L"
            );
            
            if (sizeOption) {
              const sizeKey = this.mapSizeToKey(sizeOption);
              if (sizeKey) {
                variants[sizeKey] = variant.id.toString();
              }
            }
          });
        }
      }
    } catch (e) {
      console.log("Could not parse product JSON:", e);
    }
    
    // Method 2: Check for variant selectors in the form
    try {
      const variantInputs = document.querySelectorAll('input[name="id"][data-size], option[data-variant-id]');
      variantInputs.forEach(input => {
        const size = input.dataset.size || input.textContent?.trim();
        const variantId = input.value || input.dataset.variantId;
        if (size && variantId) {
          const sizeKey = this.mapSizeToKey(size);
          if (sizeKey) {
            variants[sizeKey] = variantId.toString();
          }
        }
      });
    } catch (e) {
      console.log("Could not extract variants from form:", e);
    }
    
    // Method 3: Check window.productVariants if available
    if (window.productVariants) {
      try {
        Object.entries(window.productVariants).forEach(([key, value]) => {
          const sizeKey = this.mapSizeToKey(key);
          if (sizeKey) {
            variants[sizeKey] = value.toString();
          }
        });
      } catch (e) {
        console.log("Could not use window.productVariants:", e);
      }
    }
    
    return variants;
  }
  
  /**
   * Map various size formats to our standard S/M/L keys
   */
  mapSizeToKey(sizeString) {
    if (!sizeString) return null;
    
    const size = sizeString.toString().toUpperCase();
    
    // Direct matches
    if (size === "S" || size === "SMALL") return "S";
    if (size === "M" || size === "MEDIUM") return "M";
    if (size === "L" || size === "LARGE") return "L";
    
    // Size in cm
    if (size.includes("20X30") || size.includes("20 X 30")) return "S";
    if (size.includes("30X40") || size.includes("30 X 40")) return "M";
    if (size.includes("50X70") || size.includes("50 X 70")) return "L";
    
    return null;
  }

  /**
   * Detect whether we're on a pixar or face-swap template page
   */
  detectTemplateType() {
    if (document.body.classList.contains("template-product--with-faceswap")) {
      return "face-swap";
    }

    // Look for face-swap elements in the DOM
    if (document.querySelector("face-swap-file-input-wrapper")) {
      return "face-swap";
    }

    // Look for pixar transform elements
    if (document.querySelector("pixar-transform-file-input")) {
      return "pixar";
    }

    // Check URL parameters and template values in page metadata
    if (
      window.location.search.includes("faceswap") ||
      document
        .querySelector('meta[property="og:template"]')
        ?.content?.includes("faceswap")
    ) {
      return "face-swap";
    }

    // Default to pixar template
    return "pixar";
  }

  /**
   * Show the result popup with the processed image
   */
  showResultPopup(imageUrl) {
    console.log("RESULT POPUP: Showing result popup with image:", imageUrl);

    // Prevent showing multiple times
    if (this.resultPopupShown) {
      console.log("RESULT POPUP: Already shown, not showing again");
      return;
    }

    // Get dynamic pricing based on current currency
    let prices = { S: { formattedPrice: '£45', size: '20x30"' },
                   M: { formattedPrice: '£55', size: '30x40"' },
                   L: { formattedPrice: '£70', size: '50x70"' } };
    
    if (window.isPetTemplate && window.PetPricing && window.shopifyData) {
      const currencyCode = window.shopifyData.currency.code || 'GBP';
      console.log("RESULT POPUP: Using currency:", currencyCode);
      prices = window.PetPricing.getAllPetPrices(currencyCode);
    }

    // Add custom styles to document
    const styleElement = document.createElement("style");
    styleElement.id = "final-popup-styles";
    styleElement.textContent = `
      #pixar-result-revert:hover {
          background-color: #636363 !important;
      }
      @media (max-width: 769px) {
        #pixar-result-image-container {
            max-height: fit-content;
            height: fit-content;
        }
        #pixar-image-shadow {
            height: fit-content !important;
        }
      }
      @media (max-width: 450px) {
        #final-popup {
            max-height: 700px !important;
        }
      }
    `;

    document.head.appendChild(styleElement);

    this.resultPopupShown = true;

    // Store the original image URL for aspect ratio adjustments
    this.resultImageUrl = imageUrl;

    // Check if popup already exists
    let resultPopup = document.getElementById("pixar-result-popup");

    // Create the popup if it doesn't exist
    if (!resultPopup) {
      // Create the standard popup
      resultPopup = document.createElement("div");
      resultPopup.id = "pixar-result-popup";
      resultPopup.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.98);
        z-index: 99999999;
        display: none;
        overflow: auto;
        padding: 20px;
        box-sizing: border-box;
      `;

      // Add content to the result popup
      resultPopup.innerHTML = `
        <div id="final-popup" style="position: relative; max-width: 600px; margin: 20px auto; padding: 20px; background: white; border-radius: 12px; box-shadow: 0 0 30px rgba(0,0,0,0.1); display: flex; flex-direction: column; height: calc(100vh - 40px); max-height: 800px;">
          
          <h4 style="text-align: center; font-size: 16px; margin-top: 10px; margin-bottom: 5px; color: #000000; font-weight: bold; text-transform: uppercase;">Your preview</h4>
          <button id="close-final-image" style="position: absolute; top: 10px; right: 10px; background: none; border: none; font-size: 30px; padding: 5px; color: #555;">×</button>
          <div id="pixar-result-image-container" style="flex: 1; min-height: 0; margin: 0 auto; width: 100%; max-width: 400px; position: relative; display: flex; align-items: center; justify-content: center; padding: 10px;">
            <div id="pixar-image-shadow" style="position: absolute; top: 15px; left: 15px; right: 15px; bottom: 5px; border-radius: 8px; background: #f5f5f5; box-shadow: 0 10px 25px rgba(0,0,0,0.2); z-index: 0;"></div>
            <img id="pixar-result-image" src="" alt="Processed image" style="position: relative; z-index: 1; max-width: 100%; max-height: 100%; display: block; object-fit: contain; transition: all 0.3s ease-in-out; border-radius: 8px; background: white;">
            <div id="pixar-result-image-overlay" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: none; z-index: 2;"></div>
          </div>

          <div class="chosesize" style="width: 90%;margin: 0 auto;padding-top: 10px;">Choose size</div>
          <!-- Fixed position controls container -->
          <div style="flex-shrink: 0; margin-top: 5px;">
            <!-- Size selection menu -->
            <div style="margin-bottom: 15px;">
              <div style="display: flex; justify-content: space-between; margin: 0 auto; max-width: 90%;">
                <!-- Size S -->
                <div data-size="S" style="flex: 1; margin: 0 5px; text-align: center; border: 1px solid #ddd; border-radius: 8px; padding: 8px; cursor: pointer; background-color: #f0f5fb;">
                  <div style="width: 40px; height: 40px; background-color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto; font-weight: bold; font-size: 18px;">S</div>
                  <div style="font-weight: bold; margin-top: 5px; font-size: 16px;">${prices.S.formattedPrice}</div>
                  <div style="font-size: 12px; color: #000000; margin-top: 3px;">${prices.S.size}</div>
                </div>
                
                <!-- Size M -->
                <div data-size="M" style="flex: 1; margin: 0 5px; text-align: center; border: 1px solid #ddd; border-radius: 8px; padding: 8px; cursor: pointer;">
                  <div style="width: 40px; height: 40px; background-color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto; font-weight: bold; font-size: 18px;">M</div>
                  <div style="font-weight: bold; margin-top: 5px; font-size: 16px;">${prices.M.formattedPrice}</div>
                  <div style="font-size: 12px; color: #000000; margin-top: 3px;">${prices.M.size}</div>
                </div>
                
                <!-- Size L -->
                <div data-size="L" style="flex: 1; margin: 0 5px; text-align: center; border: 1px solid #ddd; border-radius: 8px; padding: 8px; cursor: pointer;">
                  <div style="width: 40px; height: 40px; background-color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto; font-weight: bold; font-size: 18px;">L</div>
                  <div style="font-weight: bold; margin-top: 5px; font-size: 16px;">${prices.L.formattedPrice}</div>
                  <div style="font-size: 12px; color: #000000; margin-top: 3px;">${prices.L.size}</div>
                </div>
              </div>
            
            </div>
            
            <div style="display: flex; justify-content: center; gap: 10px; margin-top: 10px; margin-bottom: 10px; flex-wrap: nowrap;">
            ${
              window?.isPetTemplate
                ? '<button id="pixar-result-revert" style="background-color: #838383; color: white; padding: 12px 25px; font-size: 16px; font-weight: bold; border: none; border-radius: 8px; cursor: pointer; text-transform: uppercase; box-shadow: 0 4px 8px rgba(0,0,0,0.1); transition: background-color 0.2s ease-in-out; white-space: nowrap;">GO BACK</button>'
                : ""
            }
              <button id="pixar-result-continue" style="background-color: #4A7DBD; color: white; padding: 12px 25px; font-size: 16px; font-weight: bold; border: none; border-radius: 8px; cursor: pointer; text-transform: uppercase; box-shadow: 0 4px 8px rgba(0,0,0,0.1); transition: background-color 0.2s ease-in-out; white-space: nowrap;">ADD TO CART</button>
            </div>
          </div>
        </div>
      `;

      // Add the popup to the document body
      document.body.appendChild(resultPopup);

      // Add event listeners for size buttons first
      const sizeOptions = resultPopup.querySelectorAll("[data-size]");
      sizeOptions.forEach((option) => {
        // Add hover effect for size options
        option.addEventListener("mouseenter", () => {
          if (option.getAttribute("data-size") !== this.selectedSize) {
            option.style.backgroundColor = "#f8f8f8";
          }
        });

        option.addEventListener("mouseleave", () => {
          if (option.getAttribute("data-size") !== this.selectedSize) {
            option.style.backgroundColor = "";
          }
        });

        option.addEventListener("click", () => {
          // Get the selected size
          const size = option.getAttribute("data-size");

          // If same size is clicked, do nothing
          if (this.selectedSize === size) return;

          // Store previous size for transition
          const previousSize = this.selectedSize;
          this.selectedSize = size;

          // Remove highlight from all options
          sizeOptions.forEach((opt) => {
            opt.style.backgroundColor = "";
            opt.style.boxShadow = "none";
          });

          // Highlight selected option
          option.style.backgroundColor = "#f0f5fb";
          option.style.boxShadow = "0 4px 8px rgba(0,0,0,0.08)";

          // Apply the appropriate aspect ratio to the image with animation
          this.applyAspectRatioToResultImage(size, previousSize);
        });
      });

      const closeButton = document.getElementById("close-final-image");
      if (closeButton) {
        closeButton.addEventListener("click", (event) => {
          event.preventDefault();
          document.body.removeAttribute("style");
          const ParentDivMainImage = document.querySelector(
            ".pixar-image-container"
          );

          if (ParentDivMainImage) {
            ParentDivMainImage.removeAttribute("style");
            ParentDivMainImage.classList.remove("pixar-image-container");
          }

          const mainImage = document.querySelector(".pixar-transformed-image");

          if (mainImage) {
            const srcObj = JSON.parse(mainImage.dataset.originalSrc);
            const attributeObj = JSON.parse(
              mainImage.dataset.originalAttributes
            );
            const stylesObj = JSON.parse(mainImage.dataset.originalStyles);

            mainImage.setAttribute("width", attributeObj.width); // or any value you want
            mainImage.setAttribute("height", attributeObj.height);

            // Set appropriate styling to ensure correct aspect ratio display
            mainImage.style.objectFit = stylesObj.objectFit;
            mainImage.style.objectPosition = stylesObj.objectPosition;
            mainImage.style.width = stylesObj.width;
            mainImage.style.height = stylesObj.height;
            mainImage.style.aspectRatio = stylesObj.aspectRatio;
            mainImage.style.maxWidth = stylesObj.maxWidth;
            mainImage.style.minWidth = stylesObj.minWidth;
            mainImage.style.minHeight = stylesObj.minHeight;
            mainImage.style.maxHeight = stylesObj.maxHeight;

            // Update the image source
            mainImage.setAttribute("src", srcObj.src);
            mainImage.setAttribute("srcset", srcObj.srcset);
            mainImage.classList.remove("pixar-transformed-image");
          }

          const uploadContainer = document.getElementById(
            "direct-pixar-loader-container"
          );
          if (uploadContainer) {
            const uploadButton = uploadContainer.querySelector("button");
            if (uploadButton) {
              uploadButton.textContent = "TRANSFORM YOUR PHOTO";
              uploadButton.style.backgroundColor = "#4a7dbd";
            }
          }
          const fileInput = pixarComponent
            ? pixarComponent.querySelector('input[type="file"]') ||
              (pixarComponent.fileInput ? pixarComponent.fileInput : null)
            : document.querySelector('input[type="file"]');
          if (fileInput) {
            fileInput.value = ""; // This resets the file input
          }
          const pixarResultPopup =
            document.getElementById("pixar-result-popup");
          pixarResultPopup.remove();

          window.imageProcessingManager.originalImageDataUrl = null;
          window.imageProcessingManager.croppedImageDataUrl = null;
          window.imageProcessingManager.transformationComplete = false;
          window.imageProcessingManager.stylizedImageUrl = null;
          window.imageProcessingManager.stylizednonImageUrl = null;

          this.resultPopupShown = false;
        });
      }

      // Add event listener for the continue button
      const revertButton = document.getElementById("pixar-result-revert");
      if (revertButton) {
        revertButton.addEventListener("click", (event) => {
          event.preventDefault();
          console.log("GO BACK button clicked");
          
          // Remove body styles
          document.body.removeAttribute("style");
          
          // Reset image container styles
          const ParentDivMainImage = document.querySelector(
            ".pixar-image-container"
          );
          if (ParentDivMainImage) {
            ParentDivMainImage.removeAttribute("style");
            ParentDivMainImage.classList.remove("pixar-image-container");
          }

          // Restore original main image
          const mainImage = document.querySelector(".pixar-transformed-image");
          if (mainImage) {
            // Check if we have original data stored
            if (mainImage.dataset.originalSrc) {
              const srcObj = JSON.parse(mainImage.dataset.originalSrc);
              const attributeObj = JSON.parse(
                mainImage.dataset.originalAttributes
              );
              const stylesObj = JSON.parse(mainImage.dataset.originalStyles);

              mainImage.setAttribute("width", attributeObj.width);
              mainImage.setAttribute("height", attributeObj.height);

              // Restore original styling
              mainImage.style.objectFit = stylesObj.objectFit;
              mainImage.style.objectPosition = stylesObj.objectPosition;
              mainImage.style.width = stylesObj.width;
              mainImage.style.height = stylesObj.height;
              mainImage.style.aspectRatio = stylesObj.aspectRatio;
              mainImage.style.maxWidth = stylesObj.maxWidth;
              mainImage.style.minWidth = stylesObj.minWidth;
              mainImage.style.minHeight = stylesObj.minHeight;
              mainImage.style.maxHeight = stylesObj.maxHeight;

              // Update the image source
              mainImage.setAttribute("src", srcObj.src);
              mainImage.setAttribute("srcset", srcObj.srcset);
            }
            mainImage.classList.remove("pixar-transformed-image");
          }

          // Check if this is a Gemini template (fully processed image)
          const isGeminiTemplate = window.imageProcessingManager && 
                                  window.imageProcessingManager.stylizedImageUrl &&
                                  window.imageProcessingManager.stylizedImageUrl.includes("gemini");
          
          if (isGeminiTemplate) {
            console.log("GO BACK: Gemini template - resetting to upload state");
            
            // Reset the image processing manager state
            if (window.imageProcessingManager) {
              window.imageProcessingManager.originalImageDataUrl = null;
              window.imageProcessingManager.croppedImageDataUrl = null;
              window.imageProcessingManager.transformationComplete = false;
              window.imageProcessingManager.stylizedImageUrl = null;
              window.imageProcessingManager.stylizednonImageUrl = null;
              window.imageProcessingManager.cropComplete = false;
              window.imageProcessingManager.textProcessingComplete = false;
              window.imageProcessingManager.isProcessing = false;
              window.imageProcessingManager.finalProcessingComplete = false;
              window.imageProcessingManager.jobId = null;
              window.imageProcessingManager.fileIdentifier = null;
            }
            
            // Reset global Railway tracking variables
            if (window.railwayJobsStatus) window.railwayJobsStatus = {};
            if (window.railwayJobsEventDispatched) window.railwayJobsEventDispatched = {};
            if (window.railwayApiCallsInProgress) window.railwayApiCallsInProgress = {};
            if (window.railwayApiCallTimestamps) window.railwayApiCallTimestamps = {};
            
            // Reset the upload button
            const uploadContainer = document.getElementById("direct-pixar-loader-container");
            if (uploadContainer) {
              const uploadButton = uploadContainer.querySelector("button");
              if (uploadButton) {
                // Reset button to initial state with clearer retry text
                uploadButton.textContent = "📸 UPLOAD PHOTO - TRY AGAIN";
                uploadButton.disabled = false;
                uploadButton.style.backgroundColor = "#4a7dbd";
                
                // Reset to original text after a delay
                setTimeout(() => {
                  if (uploadButton) {
                    uploadButton.textContent = "📸 UPLOAD YOUR PHOTO FIRST";
                  }
                }, 3000);
              }
              
              // Clear the file input
              const fileInput = uploadContainer.querySelector('input[type="file"]');
              if (fileInput) {
                fileInput.value = "";
              }
            }
            
            // Hide any remaining popups
            const loadingPopup = document.getElementById("pixar-loading-popup");
            if (loadingPopup) {
              loadingPopup.style.display = "none";
            }
            
            // Show the instructions popup with retry message
            const instructionsPopup = document.getElementById("pixar-instructions-popup");
            if (instructionsPopup) {
              instructionsPopup.style.display = "block";
              document.body.style.overflow = "hidden";
              
              // Show retry message if it exists
              const retryMessage = document.getElementById("retry-message");
              if (retryMessage) {
                retryMessage.style.display = "block";
                // Hide the message after 5 seconds
                setTimeout(() => {
                  if (retryMessage) {
                    retryMessage.style.display = "none";
                  }
                }, 5000);
              }
              
              console.log("GO BACK: Reopened upload popup with retry message");
            }
          } else {
            // For non-Gemini templates, show the image cropper
            console.log("GO BACK: Non-Gemini template - showing image cropper");
            window.imageProcessingManager.showImageCropper();
          }
          
          // Remove the result popup
          const pixarResultPopup = document.getElementById("pixar-result-popup");
          if (pixarResultPopup) {
            pixarResultPopup.remove();
          }

          this.resultPopupShown = false;
        });
      }

      // Add event listener for the continue button
      const continueButton = document.getElementById("pixar-result-continue");
      if (continueButton) {
        // Add click event listener
        continueButton.addEventListener("click", (event) => {
          // Prevent default action
          event.preventDefault();

          // Show loading animation immediately when clicked
          const button = event.currentTarget;
          const originalButtonText = button.textContent;
          button.textContent = "Adding to cart...";
          button.disabled = true;
          button.classList.add("loading");

          // Update the text shown on the popup to indicate cart redirection
          const imageContainer = document.getElementById(
            "pixar-result-image-container"
          );
          if (imageContainer) {
            // Create status message if it doesn't exist
            let statusMessage = document.getElementById("pixar-cart-status");
            if (!statusMessage) {
              statusMessage = document.createElement("div");
              statusMessage.id = "pixar-cart-status";
              statusMessage.style.cssText = `
                position: absolute;
                bottom: 10px;
                left: 0;
                right: 0;
                background: rgba(0, 0, 0, 0.7);
                color: white;
                padding: 8px;
                text-align: center;
                font-weight: bold;
                border-radius: 0 0 8px 8px;
                z-index: 10;
              `;
              imageContainer.appendChild(statusMessage);
            }
            statusMessage.textContent =
              "Adding to cart... You will be redirected shortly";
          }

          // Add loading spinner style if not already present
          if (!document.querySelector("#pixar-loading-spinner-style")) {
            const style = document.createElement("style");
            style.id = "pixar-loading-spinner-style";
            style.textContent = `
              .pixar-continue-button.loading,
              #pixar-result-continue.loading {
                position: relative;
                color: transparent !important;
                transition: background-color 0.3s ease;
              }
              .pixar-continue-button.loading::after,
              #pixar-result-continue.loading::after {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                width: 24px;
                height: 24px;
                margin: -12px 0 0 -12px;
                border-radius: 50%;
                border: 3px solid rgba(255, 255, 255, 0.8);
                border-top-color: transparent;
                animation: pixar-spinner 0.8s linear infinite;
                box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
              }
              @keyframes pixar-spinner {
                to {transform: rotate(360deg);}
              }
              /* Second animation for pulse effect */
              .pixar-continue-button.loading,
              #pixar-result-continue.loading {
                animation: pixar-pulse 2s infinite ease-in-out;
              }
              @keyframes pixar-pulse {
                0% { background-color: var(--original-bg, #4a7dbd); }
                50% { background-color: var(--pulse-bg, #3a6dad); }
                100% { background-color: var(--original-bg, #4a7dbd); }
              }
            `;
            document.head.appendChild(style);

            // Cache the original background color for pulse animation
            const computedStyle = window.getComputedStyle(button);
            const originalBg = computedStyle.backgroundColor;
            document.documentElement.style.setProperty(
              "--original-bg",
              originalBg
            );
            document.documentElement.style.setProperty(
              "--pulse-bg",
              this.adjustColor(originalBg, -20)
            );
          }

          // Disable size buttons during the transition
          const popup = document.getElementById("pixar-result-popup");
          const sizeButtons = popup.querySelectorAll("[data-size]");
          sizeButtons.forEach((btn) => {
            btn.style.pointerEvents = "none";
            btn.style.opacity = "0.7";
          });

          // Update message to indicate cart redirection is happening
          console.log(
            `Continue clicked with size ${
              this.selectedSize || "S"
            } selected, redirecting to checkout`
          );

          // Flag to indicate we're in the process of redirecting to cart
          localStorage.setItem("cartoonique_redirecting_to_cart", "true");
          localStorage.setItem("cartoonique_preload_cart_images", "true");

          // Redirect to checkout with the selected size
          this.redirectToCheckout(this.selectedSize || "S");
        });

        // Add hover effect for continue button
        continueButton.addEventListener("mouseenter", () => {
          if (!continueButton.classList.contains("loading")) {
            continueButton.style.backgroundColor = "#3a6dad";
          }
        });

        continueButton.addEventListener("mouseleave", () => {
          if (!continueButton.classList.contains("loading")) {
            continueButton.style.backgroundColor = "#4a7dbd";
          }
        });
      }

      // Set the image source
      const resultImage = document.getElementById("pixar-result-image");
      if (resultImage) {
        resultImage.src = imageUrl;

        // When the image is loaded, apply the default aspect ratio (Size S)
        resultImage.onload = () => {
          this.applyAspectRatioToResultImage(this.selectedSize);
        };
      }

      // Display the popup
      resultPopup.style.display = "block";
      document.body.style.overflow = "hidden";

      // Adjust for mobile view if needed
      this.adjustPopupForMobile();

      // If we're on a face-swap template, also update the product gallery with the image
      if (this.templateType === "face-swap") {
        this.updateProductGalleryForFaceSwap(imageUrl);
      }
    }
  }

  /**
   * Update the product gallery image for face-swap template
   */
  updateProductGalleryForFaceSwap(imageUrl) {
    if (!imageUrl) return;

    console.log(
      "Updating product gallery for face-swap template with image:",
      imageUrl
    );

    // Find all relevant image elements that need updating
    const imagesToUpdate = [
      // Main product image
      document.querySelector(".product__media-item.is-active img"),
      document.querySelector(".product-gallery__media.is-selected img"),
      document.querySelector(".product-gallery__media.snap-center img"),
      document.querySelector(".product-gallery__media img"),
      document.querySelector(".product-gallery img"),
      document.querySelector(".product__media img"),

      // Thumbnails
      ...Array.from(
        document.querySelectorAll(".product-gallery__thumbnail img")
      ),
    ].filter(Boolean); // Remove null/undefined entries

    // Update each image found
    if (imagesToUpdate.length > 0) {
      imagesToUpdate.forEach((img) => {
        // Save original src for potential restoration
        if (!img.dataset.originalSrc) {
          img.dataset.originalSrc = img.src;
        }

        // Update the image src
        img.src = imageUrl;

        // Try to update srcset if it exists
        if (img.srcset) {
          img.srcset = imageUrl;
        }
      });

      console.log(
        `Updated ${imagesToUpdate.length} product images with transformed design`
      );
    } else {
      console.warn("Could not find any product images to update");

      // Fallback approach - query all images and look for ones that might be product-related
      const allImages = document.querySelectorAll("img");
      let updatedCount = 0;

      allImages.forEach((img) => {
        const src = img.src || "";
        // Only update images that appear to be product-related
        if (
          (src.includes("product") || src.includes("files/")) &&
          !img.closest("#pixar-result-image-container") &&
          !img.closest("[data-result-wrapper]")
        ) {
          // Save original src for potential restoration
          if (!img.dataset.originalSrc) {
            img.dataset.originalSrc = img.src;
          }

          // Update the image src
          img.src = imageUrl;
          updatedCount++;
        }
      });

      if (updatedCount > 0) {
        console.log(
          `Fallback approach: Updated ${updatedCount} potential product images`
        );
      }
    }

    // Hide any result wrappers to prevent duplicate images
    const resultWrappers = document.querySelectorAll(
      "[data-result-wrapper], .result-wrapper"
    );
    resultWrappers.forEach((wrapper) => {
      wrapper.style.display = "none";
    });
  }

  /**
   * Adjust popup layout for mobile devices
   */
  adjustPopupForMobile() {
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      const popup = document.getElementById("pixar-result-popup");
      if (popup) {
        const popupContent = popup.querySelector("div");
        if (popupContent) {
          popupContent.style.margin = "0 auto";
          popupContent.style.maxWidth = "100%";
          popupContent.style.borderRadius = "0";
          popupContent.style.height = "100%";
        }
      }
    }
  }

  /**
   * Apply the appropriate aspect ratio to the result image
   */
  applyAspectRatioToResultImage(size, previousSize) {
    // Get the aspect ratio for the selected size
    const aspectRatio = this.sizeAspectRatios[size];
    if (!aspectRatio) return;

    console.log(`Applying aspect ratio ${aspectRatio} for size ${size}`);

    // Get the image element and container
    const image = document.getElementById("pixar-result-image");
    const container = document.getElementById("pixar-result-image-container");
    const shadow = document.getElementById("pixar-image-shadow");
    if (!image || !container) return;

    // Set container's aspect ratio while maintaining flexibility
    container.style.position = "relative";
    container.style.display = "flex";
    container.style.alignItems = "center";
    container.style.justifyContent = "center";
    container.style.background = "transparent";

    // Create canvas to maintain aspect ratio
    const canvas = document.createElement("canvas");

    // const container = document.getElementById('pixar-result-image-container');

    // if (canvas && container) {
    //   container.appendChild(canvas);
    // }

    const ctx = canvas.getContext("2d");

    if (!window.imageProcessingManager.processwaterimg) {
      return false;
    }

    let img = window.imageProcessingManager.processwaterimg;

    let srcX, srcY, srcW, srcH, canvasWidth, canvasHeight;

    console.log("this.cropCoordinates : ");
    console.log(window.imageProcessingManager.cropCoordinates);

    if (!window.imageProcessingManager.cropCoordinates) {
      // Use the full image
      srcX = 0;
      srcY = 0;
      srcW = img.width;
      srcH = img.height;
    } else {
      // Use the crop coordinates
      srcX = window.imageProcessingManager.croppedImageX;
      srcY = window.imageProcessingManager.croppedImageY;
      srcW = window.imageProcessingManager.croppedImageWidth;
      srcH = window.imageProcessingManager.croppedImageHeight;
    }

    // Adjust to maintain aspect ratio
    let adjustedX = srcX;
    let adjustedY = srcY;
    let adjustedW = srcW;
    let adjustedH = srcH;
    const currentAspect = srcW / srcH;

    if (currentAspect > aspectRatio) {
      // Too wide – reduce width
      const newW = srcH * aspectRatio;
      adjustedX += (srcW - newW) / 2;
      adjustedW = newW;
    } else if (currentAspect < aspectRatio) {
      // Too tall – reduce height
      const newH = srcW / aspectRatio;
      adjustedY += (srcH - newH) / 2;
      adjustedH = newH;
    }

    // Set canvas to match adjusted crop area
    canvasWidth = canvas.width = adjustedW;
    canvasHeight = canvas.height = adjustedH;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw the image (full or cropped), aspect-ratio-adjusted

    console.log(
      adjustedX,
      adjustedY,
      adjustedW,
      adjustedH,
      0,
      0,
      canvas.width,
      canvas.height
    );

    ctx.drawImage(
      img,
      adjustedX,
      adjustedY,
      adjustedW,
      adjustedH,
      0,
      0,
      canvas.width,
      canvas.height
    );

    if (window.imageProcessingManager.textInfo) {
      window.imageProcessingManager.drawTextOnCanvas(
        ctx,
        canvas.width,
        canvas.height
      );
    }

    // Replace the image src with the canvas data
    image.src = window.imageProcessingManager.processwaterimgbase64 =
      canvas.toDataURL("image/png");

    // resize the canvas to 200px
    const maxWidth = 200;
    const scaleFactor = maxWidth / canvasWidth;
    const newWidth = maxWidth;
    const newHeight = canvasHeight * scaleFactor;

    // Create a new canvas for resizing
    const resizedCanvas = document.createElement("canvas");
    resizedCanvas.width = newWidth;
    resizedCanvas.height = newHeight;

    const ctxre = resizedCanvas.getContext("2d");
    ctxre.drawImage(canvas, 0, 0, newWidth, newHeight);

    this.resizedCanvas64 = resizedCanvas.toDataURL("image/png");

    //draw non watermarked image now
    if (window.imageProcessingManager.processnonwaterimg) {
      this.selectedSizewatermark = size;

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      ctx.drawImage(
        window.imageProcessingManager.processnonwaterimg,
        adjustedX,
        adjustedY,
        adjustedW,
        adjustedH,
        0,
        0,
        canvas.width,
        canvas.height
      );

      if (window.imageProcessingManager.textInfo) {
        window.imageProcessingManager.drawTextOnCanvas(
          ctx,
          canvas.width,
          canvas.height
        );
      }

      window.imageProcessingManager.processnonwaterimgbase64 =
        canvas.toDataURL("image/png");
      console.log(
        "SD : window.imageProcessingManager.processnonwaterimgbase64 -> "
      );
      console.log(window.imageProcessingManager.processnonwaterimgbase64);
    } else {
      console.log("applyonce ");
      this.selectedSizewatermark = "applyonce";
    }

    console.log("window.imageProcessingManager?.processnonwaterimgstatus : ");
    console.log(window.imageProcessingManager?.processnonwaterimgstatus);

    const continueButton = document.getElementById("pixar-result-continue");
    if (continueButton) {
      continueButton.disabled =
        window.imageProcessingManager?.processnonwaterimgstatus == "processed"
          ? false
          : true;
      continueButton.style.setProperty(
        "cursor",
        window.imageProcessingManager?.processnonwaterimgstatus == "processed"
          ? "pointer"
          : "not-allowed",
        "important"
      );
    }

    // Adjust shadow to match image dimensions once loaded
    image.onload = () => {
      this.adjustShadowToImage(image, shadow);
    };

    // Adjust container to maintain the aspect ratio without affecting layout
    image.style.maxWidth = "100%";
    image.style.maxHeight = "100%";
    image.style.objectFit = "contain";
    image.style.borderRadius = "8px";
  }

  /**
   * Adjust the shadow to match the image dimensions
   */
  adjustShadowToImage(image, shadow) {
    if (!image || !shadow) return;

    // We need to wait for the image to be fully rendered to get its displayed dimensions
    setTimeout(() => {
      // Get the actual displayed dimensions of the image
      const rect = image.getBoundingClientRect();
      const displayedWidth = rect.width;
      const displayedHeight = rect.height;

      // Adjust the shadow to match the image size with a slight offset
      shadow.style.width = `${displayedWidth}px`;
      shadow.style.height = `${displayedHeight}px`;
      shadow.style.top = "10px";
      shadow.style.left = "50%";
      shadow.style.transform = "translateX(-50%)";
      shadow.style.borderRadius = "8px";
    }, 50); // Small delay to ensure image is rendered
  }

  /**
   * Adjust a color's brightness
   */
  adjustColor(color, amount) {
    // Handle different color formats
    let r, g, b;

    if (color.startsWith("rgb")) {
      // Parse RGB format
      const rgbMatch = color.match(
        /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/
      );
      if (rgbMatch) {
        r = parseInt(rgbMatch[1]);
        g = parseInt(rgbMatch[2]);
        b = parseInt(rgbMatch[3]);
      } else {
        return color; // Can't parse, return original
      }
    } else if (color.startsWith("#")) {
      // Parse hex format
      const hex = color.substring(1);
      r = parseInt(hex.substr(0, 2), 16);
      g = parseInt(hex.substr(2, 2), 16);
      b = parseInt(hex.substr(4, 2), 16);
    } else {
      return color; // Unsupported format, return original
    }

    // Adjust brightness
    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));

    return `rgb(${r}, ${g}, ${b})`;
  }

  /**
   * Redirect to checkout with the selected size
   */
  async redirectToCheckout(size) {
    console.log("Adding to cart with size:", size);
    console.log("Available variant IDs:", this.variantIds);

    // Get the variant ID for the selected size
    const variantId = this.variantIds[size];

    if (!variantId) {
      console.error("Invalid size selected:", size);
      console.error("Available sizes:", Object.keys(this.variantIds));
      alert("Error: Invalid product variant. Please refresh the page and try again.");
      return;
    }
    
    console.log("Using variant ID:", variantId, "for size:", size);

    // Get the processed image URL
    let processedImageUrl = this.resultImageUrl;
    console.log(
      "Found image from resultImageUrl:",
      processedImageUrl ? "yes" : "no"
    );

    // If not found, try to get it from the DOM
    if (!processedImageUrl) {
      const resultImage = document.getElementById("pixar-result-image");
      if (resultImage && resultImage.src) {
        processedImageUrl = resultImage.src;
      }
    }

    if (!processedImageUrl) {
      console.error(
        "No processed image found - will attempt to continue anyway"
      );
      processedImageUrl = "";
    }

    // Store the image safely
    // this.storeImageSafely(variantId, processedImageUrl);

    // Direct redirect function without an iframe sd 22 save img
    const redirectToCart = async () => {
      console.log("Executing immediate redirect to cart...");

      try {
        let response = await fetch("/cart.js"); // Replace with actual API endpoint
        if (!response.ok) {
          throw new Error("Failed to fetch cart data");
        }
        let cartData = await response.json();

        console.log("cartData -> ");
        console.log(cartData);

        let storedImages =
          JSON.parse(localStorage.getItem("cartooniqueImages")) || {};

        console.log("storedImages");
        console.log(storedImages);

        let updatedImages = {}; // New object for cleaned-up images

        // Assign the first cart item's image to currentBase64
        if (cartData?.items?.length > 0) {
          updatedImages[cartData.items[0].key] = this.resizedCanvas64;

          // Iterate over the cart and assign images from localStorage if available
          cartData.items.forEach((item) => {
            if (storedImages?.[item.key]) {
              updatedImages[item.key] = storedImages[item.key] || ""; // Keep blank if missing
            }
          });

          // Save the updated object to localStorage
        }

        localStorage.setItem(
          "cartooniqueImages",
          JSON.stringify(updatedImages)
        );
      } catch (e) {
        console.error("Error updating localstorage :", e);
      }

      // Fallback: The original navigation method if the above fails
      window.location.href = "/cart?nocache=" + Date.now();
    };
    let cartproperties = {};

    // Get URLs from status response instead of uploading new images
    if (window.imageProcessingManager?.jobId) {
      console.log("Using URLs from status endpoint instead of uploading to S3");
      
      try {
        // Get the status response with the processed URLs
        const statusResponse = await fetch(
          `https://letzteshemd-faceswap-api-production.up.railway.app/status/${window.imageProcessingManager.jobId}`
        );
        
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          console.log("Status response data:", statusData);
          
          // Use URLs from status response for order properties
          if (statusData.watermarkedImageUrlToShow) {
            cartproperties["_uploadedimg"] = statusData.watermarkedImageUrlToShow; // Watermarked for display
            console.log("Set _uploadedimg from status response:", statusData.watermarkedImageUrlToShow);
          }
          
          if (statusData.processedPrintImageUrl) {
            cartproperties["_printimg"] = statusData.processedPrintImageUrl; // Non-watermarked with canvas bleed
            console.log("Set _printimg from status response:", statusData.processedPrintImageUrl);
          }
        } else {
          console.warn("Failed to fetch status response, falling back to upload method");
          
          // Fallback to original upload method if status endpoint fails
          if (window.imageProcessingManager?.processnonwaterimgbase64) {
            async function uploadImageToS3(base64Image, fileName) {
              const apiUrl = "https://letzteshemd-faceswap-api-production.up.railway.app/upload";
              
              try {
                const response = await fetch(apiUrl, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    name: fileName,
                    base64Image: base64Image,
                  }),
                });
                
                const result = await response.json();
                if (!response.ok) {
                  throw new Error(result.error || "Failed to upload image");
                }
                
                console.log("Fallback: Image uploaded successfully:", result.url);
                return result.url;
              } catch (error) {
                console.error("Fallback: Error uploading image:", error.message);
                return null;
              }
            }

            const nonwaterimgBase64 = window.imageProcessingManager.processnonwaterimgbase64;
            const fileName = variantId + "_" + Date.now();
            
            const uploadedUrl = await uploadImageToS3(nonwaterimgBase64, fileName);
            if (uploadedUrl) {
              cartproperties["_nonwatermarkimg"] = uploadedUrl;
              console.log("Fallback: Set _nonwatermarkimg to uploaded URL:", uploadedUrl);
            }
          }
        }
      } catch (error) {
        console.error("Error getting status or uploading image:", error);
      }
    }

    // Simple cart addition data
    const formDataSimple = {
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: cartproperties,
        },
      ],
    };

    // Add to cart via API
    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(formDataSimple),
    })
      .then((response) => {
        if (!response.ok) {
          console.error("Cart addition error with status:", response.status);
          console.error("Attempted variant ID:", variantId);
          console.error("Size selected:", size);
          return response.text().then((text) => {
            console.error("Error response:", text);
            
            // Parse error message for more details
            try {
              const errorData = JSON.parse(text);
              if (errorData.description === "Cannot find variant") {
                console.error("Variant ID does not exist in Shopify:", variantId);
                alert(`Error: Product variant not found for size ${size}. Please contact support.`);
              }
            } catch (e) {
              // Not JSON, continue with generic error
            }
            
            throw new Error(
              "Network response was not ok: " + response.statusText
            );
          });
        }
        return response.json();
      })
      .then((data) => {
        console.log("Successfully added to cart:", data);
        redirectToCart();
      })
      .catch((error) => {
        console.error("Error adding to cart:", error);

        // Reset the button state on error
        const continueButton = document.getElementById("pixar-result-continue");
        if (continueButton) {
          continueButton.textContent = "Continue";
          continueButton.disabled =
            window.imageProcessingManager?.processnonwaterimgstatus ==
            "processed"
              ? false
              : true;
          continueButton.classList.remove("loading");
        }

        // Re-enable size buttons
        const sizeButtons = document.querySelectorAll("[data-size]");
        if (sizeButtons) {
          sizeButtons.forEach((button) => {
            button.disabled = false;
          });
        }

        // Try with a direct form submission as last resort
        const form = document.createElement("form");
        form.method = "post";
        form.action = "/cart/add";

        const idInput = document.createElement("input");
        idInput.type = "hidden";
        idInput.name = "id";
        idInput.value = variantId;

        const quantityInput = document.createElement("input");
        quantityInput.type = "hidden";
        quantityInput.name = "quantity";
        quantityInput.value = "1";

        form.appendChild(idInput);
        form.appendChild(quantityInput);
        document.body.appendChild(form);

        // Show a message before submitting
        const errorMessage = document.createElement("div");
        errorMessage.className = "pixar-error-message";
        errorMessage.style.color = "orange";
        errorMessage.style.margin = "10px 0";
        errorMessage.style.textAlign = "center";
        errorMessage.innerHTML = `
        Trying alternate method to add to cart...
      `;

        // Find an appropriate place to show the error
        const popup = document.getElementById("pixar-result-popup");
        const messageContainer =
          (popup && popup.querySelector(".pixar-result-buttons")) ||
          (popup && popup.querySelector(".pixar-result-controls"));
        if (messageContainer) {
          messageContainer.appendChild(errorMessage);
        }

        // Submit the form after a short delay
        // setTimeout(() => {
        //   form.submit();
        // }, 1000);
      });
  }

  /**
   * Store the image safely to prevent quota errors
   */
  storeImageSafely(variantId, imageUrl) {
    try {
      // Always store the raw image data in window memory
      if (!window.cartoonique_memory_images) {
        window.cartoonique_memory_images = {};
      }
      window.cartoonique_memory_images[variantId] = imageUrl;
      console.log("Stored image in window memory for variant", variantId);

      // Try to store a reference in localStorage
      try {
        const cartImages = JSON.parse(
          localStorage.getItem("cartoonique_cart_images") || "{}"
        );
        // Store just a reference to the memory image
        cartImages[variantId] = "MEMORY_IMAGE:" + variantId;
        localStorage.setItem(
          "cartoonique_cart_images",
          JSON.stringify(cartImages)
        );

        // Also store navigation data for immediate use
        const navigationData = {
          timestamp: Date.now(),
          imageUrl: imageUrl,
          variantId: variantId,
        };
        localStorage.setItem(
          "cartoonique_navigation_data",
          JSON.stringify(navigationData)
        );
      } catch (e) {
        console.warn("Could not store image reference in localStorage:", e);
      }

      return true;
    } catch (error) {
      console.error("Storage error:", error);
      return false;
    }
  }
}

// Initialize the ResultPopupManager when the script loads
document.addEventListener("DOMContentLoaded", () => {
  new ResultPopupManager();
});

// Export for direct use
if (typeof module !== "undefined" && module.exports) {
  module.exports = ResultPopupManager;
}
